import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import MediaLibrary from '../components/MediaLibrary';
import MediaItemsField from '../components/MediaItemsField';
import IconPicker from '../components/IconPicker';
import CustomLayoutThumb from '../components/CustomLayoutThumb';
import FieldEditor from '../components/FieldEditor';
import { DesignerSection, DESIGNER_SECTION_TYPES } from '../components/DesignerSections';
import AdminLayoutBuilder from './AdminLayoutBuilder';
import { AvatarUpload } from '../components/AvatarUpload';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import {
  Eye, EyeOff, Save, RotateCcw, Loader2, GripVertical, Plus, Copy, Archive,
  Layout, Layers, Image, Globe, Settings, History, Download, ExternalLink, Check, X,
  AlertTriangle, AlertCircle, ChevronDown, ChevronUp, Edit2, Trash2, RefreshCw,
  FileText, Link, Type, Calendar, Hash, List, ToggleLeft, Upload
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============== Sub-Components ==============

const SectionCard = ({ section, onEdit, onToggleVisibility, onDuplicate, onDelete, onDragStart, onDragOver, onDrop }) => {
  const statusColors = {
    draft: 'bg-amber-100 text-amber-700',
    published: 'bg-green-100 text-green-700',
    archived: 'bg-gray-100 text-gray-500'
  };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, section.id)}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, section.id)}
      className={`bg-white border rounded-lg p-4 mb-3 transition-all hover:shadow-md ${
        !section.visible ? 'opacity-60' : ''
      }`}
    >
      <div className="flex items-center gap-4">
        <div className="cursor-grab text-gray-400 hover:text-gray-600">
          <GripVertical className="w-5 h-5" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-medium text-gray-900">{section.name}</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[section.status]}`}>
              {section.status}
            </span>
          </div>
          <p className="text-sm text-gray-500">
            Type: {section.section_type} • Updated: {new Date(section.updated_at).toLocaleDateString()}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => onToggleVisibility(section.id, !section.visible)}
            className={`p-2 rounded-md transition-colors ${
              section.visible ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'
            }`}
            title={section.visible ? 'Hide section' : 'Show section'}
          >
            {section.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
          
          <button
            onClick={() => onEdit(section)}
            data-testid={`section-edit-${section.section_type}`}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
            title="Edit section"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          
          <button
            onClick={() => onDuplicate(section.id)}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
            title="Duplicate section"
          >
            <Copy className="w-4 h-4" />
          </button>
          
          <button
            onClick={() => onDelete(section.id)}
            className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
            title="Delete section"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

const SectionEditor = ({ section, template, onSave, onClose }) => {
  const [content, setContent] = useState(section.draft_content || section.content || {});
  const [saving, setSaving] = useState(false);
  const [showCanvas, setShowCanvas] = useState(false);
  const isDesigner = DESIGNER_SECTION_TYPES.has(section.section_type);
  const isCustomLayout = section.section_type === 'custom_layout';

  const handleFieldChange = (fieldName, value) => {
    setContent(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleCanvasSave = (nextLayout) => {
    // Merge the builder output back into this section's content in-place.
    setContent(prev => ({
      ...prev,
      eyebrow: nextLayout.eyebrow ?? prev.eyebrow,
      section_title: nextLayout.section_title ?? prev.section_title,
      section_subtitle: nextLayout.section_subtitle ?? prev.section_subtitle,
      accent_color: nextLayout.accent_color ?? prev.accent_color,
      background: nextLayout.background ?? prev.background,
      canvas_width: nextLayout.canvas_width ?? prev.canvas_width,
      canvas_height: nextLayout.canvas_height ?? prev.canvas_height,
      boxes: nextLayout.boxes || [],
    }));
    setShowCanvas(false);
    toast.success('Canvas updated — click Save to persist to this page.');
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(section.id, content);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex justify-end z-50">
      <div className={`w-full ${isDesigner ? 'max-w-[95vw] lg:max-w-6xl' : 'max-w-xl'} bg-white h-full flex flex-col overflow-hidden`}>
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between z-10 flex-shrink-0">
          <div>
            <h2 className="font-bold text-lg">{section.name}</h2>
            <p className="text-sm text-gray-500">Type: {section.section_type}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="section-editor-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Save
            </Button>
          </div>
        </div>

        {isDesigner ? (
          <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
            {/* Live preview pane */}
            <div
              className="lg:w-3/5 lg:border-r bg-slate-100 overflow-y-auto"
              data-testid="section-live-preview"
            >
              <div className="sticky top-0 z-10 bg-slate-100/95 backdrop-blur px-4 py-2 text-[11px] font-semibold uppercase tracking-widest text-slate-500 border-b flex items-center justify-between">
                <span>Live Preview</span>
                <span className="text-slate-400 normal-case tracking-normal text-[10px]">
                  Updates as you type — not saved until you click Save
                </span>
              </div>
              <div className="scale-[0.85] origin-top-left w-[117.6%]">
                <DesignerSection section={{ ...section, content }} />
              </div>
            </div>
            {/* Form */}
            <div className="lg:w-2/5 p-6 space-y-6 overflow-y-auto">
              {isCustomLayout ? (
                <>
                  <div className="rounded-lg border-2 border-dashed border-[#00457f]/30 bg-[#00457f]/5 p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <Layout className="w-6 h-6 text-[#00457f] flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <h4 className="font-semibold text-sm text-slate-800">Custom Canvas Layout</h4>
                        <p className="text-xs text-slate-600 mt-0.5">
                          This section uses a freeform canvas. {content.boxes?.length || 0} panel{content.boxes?.length === 1 ? '' : 's'} placed.
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={() => setShowCanvas(true)}
                      className="w-full bg-[#00457f] hover:bg-[#003566]"
                      data-testid="edit-custom-canvas-btn"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit Custom Canvas
                    </Button>
                    <p className="text-[11px] text-slate-500 leading-snug">
                      Opens the drag-and-drop builder pre-loaded with this section&apos;s panels. Changes save into this page only — the original template stays untouched.
                    </p>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Eyebrow</label>
                      <Input
                        value={content.eyebrow || ''}
                        onChange={(e) => handleFieldChange('eyebrow', e.target.value)}
                        placeholder="Small caps text above title"
                        data-testid="custom-layout-eyebrow"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Section Title</label>
                      <Input
                        value={content.section_title || ''}
                        onChange={(e) => handleFieldChange('section_title', e.target.value)}
                        placeholder="Section heading"
                        data-testid="custom-layout-title"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Section Subtitle</label>
                      <Textarea
                        value={content.section_subtitle || ''}
                        onChange={(e) => handleFieldChange('section_subtitle', e.target.value)}
                        rows={2}
                        placeholder="Optional supporting sentence"
                        data-testid="custom-layout-subtitle"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Accent color</label>
                      <input
                        type="color"
                        value={content.accent_color || '#d4af37'}
                        onChange={(e) => handleFieldChange('accent_color', e.target.value)}
                        className="w-full h-10 rounded border"
                      />
                    </div>
                  </div>
                </>
              ) : (
                template?.fields?.map((field) => (
                  <FieldEditor
                    key={field.name}
                    field={field}
                    value={content[field.name]}
                    onChange={handleFieldChange}
                  />
                ))
              )}
            </div>
          </div>
        ) : (
          <div className="flex-1 p-6 space-y-6 overflow-y-auto">
            {template?.fields?.map((field) => (
              <FieldEditor
                key={field.name}
                field={field}
                value={content[field.name]}
                onChange={handleFieldChange}
              />
            ))}
          </div>
        )}
      </div>
      {/* Full-screen inline canvas editor for custom_layout sections */}
      {showCanvas && isCustomLayout && (
        <div className="fixed inset-0 bg-white z-[60] overflow-y-auto" data-testid="inline-layout-builder">
          <AdminLayoutBuilder
            embedded
            initialLayout={{
              name: section.name,
              eyebrow: content.eyebrow || '',
              section_title: content.section_title || '',
              section_subtitle: content.section_subtitle || '',
              accent_color: content.accent_color || '#d4af37',
              background: content.background || { type: 'none' },
              canvas_width: content.canvas_width || 1200,
              canvas_height: content.canvas_height || 600,
              boxes: content.boxes || [],
            }}
            onSaveEmbedded={handleCanvasSave}
            onCloseEmbedded={() => setShowCanvas(false)}
            embeddedSaveLabel="Apply to Section"
            embeddedTitle={`Editing: ${section.name}`}
          />
        </div>
      )}
    </div>
  );
};

const TEMPLATE_THUMBNAILS = {
  hero: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#0a1a2f" />
      <rect x="16" y="18" width="88" height="6" rx="1" fill="#d4af37" />
      <rect x="24" y="30" width="72" height="3" rx="1" fill="#ffffff88" />
      <rect x="30" y="38" width="60" height="3" rx="1" fill="#ffffff66" />
      <rect x="46" y="46" width="28" height="8" rx="4" fill="#d4af37" />
    </svg>
  ),
  about: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#f8fafc" />
      <rect x="10" y="10" width="45" height="40" rx="3" fill="#e2e8f0" />
      <rect x="62" y="14" width="48" height="4" rx="1" fill="#0a1a2f" />
      <rect x="62" y="24" width="42" height="3" rx="1" fill="#94a3b8" />
      <rect x="62" y="32" width="46" height="3" rx="1" fill="#94a3b8" />
      <rect x="62" y="40" width="36" height="3" rx="1" fill="#94a3b8" />
    </svg>
  ),
  program_highlights: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#f8fafc" />
      {[0,1,2].map(i => (
        <g key={i}>
          <rect x={8 + i*36} y="14" width="32" height="32" rx="3" fill="#fff" stroke="#e2e8f0" />
          <circle cx={13 + i*36} cy="19" r="2" fill="#00457f" />
          <rect x={12 + i*36} y="26" width="24" height="3" rx="1" fill="#0a1a2f" />
          <rect x={12 + i*36} y="33" width="20" height="2" rx="1" fill="#94a3b8" />
          <rect x={12 + i*36} y="38" width="18" height="2" rx="1" fill="#94a3b8" />
        </g>
      ))}
    </svg>
  ),
  speaker_preview: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#f8fafc" />
      {[0,1,2,3].map(i => (
        <g key={i}>
          <circle cx={22 + i*24} cy="26" r="8" fill="#e2e8f0" />
          <rect x={12 + i*24} y="40" width="20" height="2.5" rx="1" fill="#0a1a2f" />
          <rect x={14 + i*24} y="46" width="16" height="2" rx="1" fill="#94a3b8" />
        </g>
      ))}
    </svg>
  ),
  sponsors: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#ffffff" />
      {[0,1,2,3,4,5].map(i => (
        <rect key={i} x={10 + (i%3)*36} y={12 + Math.floor(i/3)*20} width="30" height="14" rx="2" fill="#e2e8f0" />
      ))}
    </svg>
  ),
  location: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#e2e8f0" />
      <rect x="10" y="12" width="100" height="36" rx="3" fill="#94a3b8" />
      <circle cx="60" cy="30" r="4" fill="#dc2626" />
      <circle cx="60" cy="30" r="8" fill="none" stroke="#dc2626" strokeOpacity="0.4" />
    </svg>
  ),
  registration_cta: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#00457f" />
      <rect x="20" y="18" width="80" height="5" rx="1" fill="#fff" />
      <rect x="30" y="28" width="60" height="3" rx="1" fill="#ffffff99" />
      <rect x="42" y="40" width="36" height="10" rx="5" fill="#d4af37" />
    </svg>
  ),
  stats: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#f8fafc" />
      {[0,1,2,3].map(i => (
        <g key={i}>
          <rect x={8 + i*28} y="18" width="24" height="24" rx="2" fill="#fff" stroke="#e2e8f0" />
          <text x={20 + i*28} y="32" textAnchor="middle" fontSize="7" fontWeight="bold" fill="#00457f">
            {['500', '30', '50', '2K'][i]}
          </text>
          <rect x={12 + i*28} y="36" width="16" height="2" rx="1" fill="#94a3b8" />
        </g>
      ))}
    </svg>
  ),
  featured_nation: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#0a1a2f" />
      <rect x="10" y="14" width="30" height="32" rx="2" fill="#d4af37" />
      <rect x="46" y="18" width="60" height="4" rx="1" fill="#fff" />
      <rect x="46" y="26" width="50" height="2.5" rx="1" fill="#ffffff88" />
      <rect x="46" y="32" width="54" height="2.5" rx="1" fill="#ffffff88" />
    </svg>
  ),
  action_cards: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#f8fafc" />
      {[0,1,2].map(i => (
        <g key={i}>
          <rect x={10 + i*36} y="12" width="32" height="36" rx="4" fill="#fff" stroke="#e2e8f0" />
          <rect x={14 + i*36} y="16" width="24" height="14" rx="2" fill="#00457f22" />
          <rect x={14 + i*36} y="34" width="20" height="3" rx="1" fill="#0a1a2f" />
          <rect x={14 + i*36} y="41" width="16" height="2" rx="1" fill="#94a3b8" />
        </g>
      ))}
    </svg>
  ),
  newsletter: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#d4af37" />
      <rect x="20" y="18" width="80" height="5" rx="1" fill="#0a1a2f" />
      <rect x="16" y="34" width="60" height="10" rx="5" fill="#fff" />
      <rect x="80" y="34" width="24" height="10" rx="5" fill="#0a1a2f" />
    </svg>
  ),
  footer_cta: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#0a1a2f" />
      <rect x="30" y="22" width="60" height="4" rx="1" fill="#fff" />
      <rect x="46" y="38" width="28" height="8" rx="4" fill="#d4af37" />
    </svg>
  ),
  media_gallery: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#f8fafc" />
      {[0,1,2,3,4,5].map(i => (
        <rect key={i} x={8 + (i%3)*36} y={10 + Math.floor(i/3)*22} width="32" height="18" rx="2" fill="#94a3b8" />
      ))}
    </svg>
  ),
  feature_grid_diagonal: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#ffffff" />
      {[0,1,2,3].map(i => (
        <g key={i} transform={`translate(${8 + i*28}, ${12 + i*6})`}>
          <rect width="24" height="26" rx="3" fill="#fff" stroke="#e2e8f0" />
          <rect x="2" y="-3" width="7" height="7" rx="1.5" fill="#d4af37" />
          <rect x="4" y="10" width="16" height="2.5" rx="1" fill="#0a1a2f" />
          <rect x="4" y="16" width="14" height="2" rx="1" fill="#94a3b8" />
          <rect x="4" y="21" width="12" height="2" rx="1" fill="#94a3b8" />
        </g>
      ))}
    </svg>
  ),
  bento_layout: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#f8fafc" />
      <rect x="8" y="8" width="66" height="44" rx="3" fill="#00457f" />
      <rect x="14" y="14" width="10" height="10" rx="2" fill="#d4af37" />
      <rect x="14" y="36" width="28" height="4" rx="1" fill="#fff" />
      <rect x="14" y="44" width="20" height="2.5" rx="1" fill="#ffffff88" />
      <rect x="78" y="8" width="34" height="13" rx="3" fill="#fff" stroke="#e2e8f0" />
      <rect x="78" y="24" width="34" height="13" rx="3" fill="#fff" stroke="#e2e8f0" />
      <rect x="78" y="40" width="34" height="12" rx="3" fill="#fff" stroke="#e2e8f0" />
    </svg>
  ),
  step_timeline: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#ffffff" />
      <line x1="60" y1="8" x2="60" y2="52" stroke="#00457f44" strokeWidth="1" />
      {[0,1,2,3].map(i => (
        <g key={i}>
          <circle cx="60" cy={12 + i*12} r="4" fill="#00457f" />
          <text x="60" y={14 + i*12} textAnchor="middle" fontSize="4" fill="#fff" fontWeight="bold">0{i+1}</text>
          {i % 2 === 0 ? (
            <>
              <rect x="20" y={9 + i*12} width="30" height="2.5" rx="1" fill="#0a1a2f" />
              <rect x="26" y={14 + i*12} width="24" height="2" rx="1" fill="#94a3b8" />
            </>
          ) : (
            <>
              <rect x="70" y={9 + i*12} width="30" height="2.5" rx="1" fill="#0a1a2f" />
              <rect x="70" y={14 + i*12} width="24" height="2" rx="1" fill="#94a3b8" />
            </>
          )}
        </g>
      ))}
    </svg>
  ),
  layered_panels: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#0a1a2f" />
      {[
        {x:14, y:10}, {x:66, y:16}, {x:14, y:34}, {x:66, y:40}
      ].map((p, i) => (
        <g key={i}>
          <rect x={p.x+2} y={p.y+2} width="42" height="18" rx="2" fill="#d4af37" opacity="0.35" />
          <rect x={p.x} y={p.y} width="42" height="18" rx="2" fill="#fff" />
          <rect x={p.x+3} y={p.y+3} width="6" height="6" rx="1" fill="#d4af3733" />
          <rect x={p.x+12} y={p.y+4} width="24" height="2.5" rx="1" fill="#0a1a2f" />
          <rect x={p.x+12} y={p.y+9} width="20" height="2" rx="1" fill="#94a3b8" />
        </g>
      ))}
    </svg>
  ),
  layered_panels_6: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#0a1a2f" />
      {[
        {x:8, y:8, s:'#d4af37'}, {x:44, y:14, s:'#00457f'}, {x:80, y:20, s:'#d4af37'},
        {x:8, y:32, s:'#00457f'}, {x:44, y:38, s:'#d4af37'}, {x:80, y:44, s:'#00457f'},
      ].map((p, i) => (
        <g key={i}>
          <rect x={p.x+1.5} y={p.y+1.5} width="30" height="14" rx="1.5" fill={p.s} opacity="0.4" />
          <rect x={p.x} y={p.y} width="30" height="14" rx="1.5" fill="#fff" />
          <rect x={p.x+2} y={p.y+2} width="5" height="5" rx="1" fill={`${p.s}33`} />
          <rect x={p.x+9} y={p.y+3} width="18" height="2" rx="1" fill="#0a1a2f" />
          <rect x={p.x+9} y={p.y+7} width="14" height="1.5" rx="1" fill="#94a3b8" />
        </g>
      ))}
    </svg>
  ),
  layered_panels_8: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#0a1a2f" />
      {[
        {x:6, y:10}, {x:34, y:14}, {x:62, y:18}, {x:90, y:22},
        {x:6, y:32}, {x:34, y:36}, {x:62, y:40}, {x:90, y:44},
      ].map((p, i) => (
        <g key={i}>
          <rect x={p.x+1} y={p.y+1} width="24" height="12" rx="1.5" fill="#d4af37" opacity="0.4" />
          <rect x={p.x} y={p.y} width="24" height="12" rx="1.5" fill="#fff" />
          <rect x={p.x+2} y={p.y+2} width="4" height="4" rx="0.5" fill="#d4af3733" />
          <rect x={p.x+8} y={p.y+2.5} width="14" height="1.5" rx="0.5" fill="#0a1a2f" />
          <rect x={p.x+8} y={p.y+6} width="12" height="1" rx="0.5" fill="#94a3b8" />
        </g>
      ))}
    </svg>
  ),
  custom: (
    <svg viewBox="0 0 120 60" className="w-full h-full">
      <rect width="120" height="60" fill="#f8fafc" />
      <rect x="20" y="18" width="80" height="24" rx="2" fill="#fff" stroke="#e2e8f0" strokeDasharray="3,3" />
      <text x="60" y="34" textAnchor="middle" fontSize="8" fill="#94a3b8">Custom</text>
    </svg>
  ),
};

const AddSectionModal = ({ templates, onAdd, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[85vh] overflow-y-auto">
        <CardHeader>
          <CardTitle>Add Section</CardTitle>
          <CardDescription>Pick a template — preview the layout below</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((template) => {
              const isCustom = template.is_custom && template.layout_preview;
              const thumb = isCustom
                ? <CustomLayoutThumb layout={template.layout_preview} />
                : (TEMPLATE_THUMBNAILS[template.section_type] || TEMPLATE_THUMBNAILS.custom);
              return (
                <button
                  key={template.id}
                  onClick={() => onAdd(template.section_type, template.id)}
                  className="group text-left rounded-xl border overflow-hidden hover:border-[#00457f] hover:shadow-lg transition-all bg-white flex flex-col"
                  data-testid={`template-card-${template.section_type}${isCustom ? '-' + template.custom_layout_id : ''}`}
                >
                  <div className="aspect-[2/1] w-full border-b bg-slate-50 flex items-center justify-center overflow-hidden">
                    {thumb}
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-sm text-[#0a1a2f] flex items-center gap-1.5">
                      {template.name}
                      {isCustom && (
                        <span className="text-[9px] font-bold uppercase tracking-widest text-[#00457f] bg-[#00457f]/10 px-1.5 py-0.5 rounded">
                          Custom
                        </span>
                      )}
                    </h3>
                    <p className="text-xs text-slate-500 mt-1 leading-snug">{template.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="mt-6 flex justify-end">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const ValidationPanel = ({ validation }) => {
  if (!validation) return null;
  
  const hasIssues = validation.errors?.length > 0 || validation.warnings?.length > 0;
  if (!hasIssues) return null;

  return (
    <div className="mb-4 space-y-2">
      {validation.errors?.map((err, idx) => (
        <div key={idx} className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-700">{err.message}</p>
            <p className="text-xs text-red-500">{err.field}</p>
          </div>
        </div>
      ))}
      {validation.warnings?.map((warn, idx) => (
        <div key={idx} className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-700">{warn.message}</p>
            <p className="text-xs text-amber-500">{warn.field}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

// ============== Main Component ==============

const AdminHomepage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('builder');
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [config, setConfig] = useState(null);
  const [versions, setVersions] = useState([]);
  const [validation, setValidation] = useState(null);
  
  const [editingSection, setEditingSection] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [draggedId, setDraggedId] = useState(null);
  const [activePage, setActivePage] = useState('homepage');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate, activePage]);

  const authHeaders = () => {
    const t = localStorage.getItem('admin_token');
    return t ? { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sectionsRes, templatesRes, configRes, versionsRes] = await Promise.all([
        fetch(`${API_URL}/api/homepage/sections?page=${encodeURIComponent(activePage)}`),
        fetch(`${API_URL}/api/homepage/templates`),
        fetch(`${API_URL}/api/homepage/config`),
        fetch(`${API_URL}/api/homepage/versions`)
      ]);

      const sectionsData = sectionsRes.ok ? await sectionsRes.json() : [];
      if (templatesRes.ok) setTemplates(await templatesRes.json());
      if (configRes.ok) setConfig(await configRes.json());
      if (versionsRes.ok) setVersions(await versionsRes.json());
      
      // If no sections, seed defaults
      if (sectionsData.length === 0) {
        await fetch(`${API_URL}/api/homepage/seed`, { method: 'POST', headers: authHeaders() });
        fetchData();
        return;
      }
      
      setSections(sectionsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleValidate = async () => {
    try {
      const response = await fetch(`${API_URL}/api/homepage/validate`, { method: 'POST', headers: authHeaders() });
      if (response.ok) {
        const result = await response.json();
        setValidation(result);
        return result;
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
    return null;
  };

  const handlePublish = async () => {
    setPublishing(true);
    const validationResult = await handleValidate();
    
    if (!validationResult?.is_valid) {
      toast.error('Please fix validation errors before publishing');
      setPublishing(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/homepage/publish`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ change_notes: '' })
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          toast.success(result.message);
          fetchData();
        } else {
          toast.error(result.message);
        }
      }
    } catch (error) {
      toast.error('Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  const handleAddSection = async (sectionType, templateId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/homepage/sections?section_type=${sectionType}&template_id=${templateId}&page=${encodeURIComponent(activePage)}`,
        { method: 'POST', headers: authHeaders() }
      );
      if (response.ok) {
        const newSection = await response.json();
        toast.success('Section added');
        await fetchData();
        setEditingSection(newSection);
      }
    } catch (error) {
      toast.error('Failed to add section');
    }
    setShowAddModal(false);
  };

  const handleToggleVisibility = async (sectionId, visible) => {
    try {
      await fetch(`${API_URL}/api/homepage/sections/${sectionId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ visible })
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to update section');
    }
  };

  const handleDuplicate = async (sectionId) => {
    try {
      await fetch(`${API_URL}/api/homepage/sections/${sectionId}/duplicate`, { method: 'POST', headers: authHeaders() });
      toast.success('Section duplicated');
      fetchData();
    } catch (error) {
      toast.error('Failed to duplicate section');
    }
  };

  const handleDelete = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this section?')) return;
    try {
      await fetch(`${API_URL}/api/homepage/sections/${sectionId}`, { method: 'DELETE', headers: authHeaders() });
      toast.success('Section deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete section');
    }
  };

  const handleSaveSection = async (sectionId, content) => {
    try {
      await fetch(`${API_URL}/api/homepage/sections/${sectionId}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ content })
      });
      toast.success('Section saved');
      fetchData();
      setEditingSection(null);
    } catch (error) {
      toast.error('Failed to save section');
    }
  };

  const handleDragStart = (e, id) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetId) => {
    e.preventDefault();
    if (!draggedId || draggedId === targetId) return;

    const draggedIndex = sections.findIndex(s => s.id === draggedId);
    const targetIndex = sections.findIndex(s => s.id === targetId);
    
    const newSections = [...sections];
    const [removed] = newSections.splice(draggedIndex, 1);
    newSections.splice(targetIndex, 0, removed);
    
    const reordered = newSections.map((s, idx) => ({ id: s.id, order: idx }));
    
    try {
      await fetch(`${API_URL}/api/homepage/sections/reorder`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(reordered)
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to reorder sections');
    }
    
    setDraggedId(null);
  };

  const handleSaveConfig = async (updates) => {
    try {
      await fetch(`${API_URL}/api/homepage/config`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(updates)
      });
      toast.success('Settings saved');
      fetchData();
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleRestoreVersion = async (versionId) => {
    if (!window.confirm('This will replace all current content. Continue?')) return;
    try {
      await fetch(`${API_URL}/api/homepage/versions/${versionId}/restore`, { method: 'POST', headers: authHeaders() });
      toast.success('Version restored');
      fetchData();
    } catch (error) {
      toast.error('Failed to restore version');
    }
  };

  const tabs = [
    { id: 'builder', label: 'Page Builder', icon: Layout },
    { id: 'media', label: 'Media & Assets', icon: Image },
    { id: 'seo', label: 'SEO & Sharing', icon: Globe },
    { id: 'video', label: 'Hero Video', icon: FileText },
    { id: 'versions', label: 'Version History', icon: History }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#00457f]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Homepage Manager</h1>
            <p className="text-gray-600">Manage your homepage content and layout</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.open('/', '_blank')}>
              <ExternalLink className="w-4 h-4 mr-1" />
              View Live
            </Button>
            <Button variant="outline" onClick={handleValidate}>
              <Check className="w-4 h-4 mr-1" />
              Validate
            </Button>
            <Button onClick={handlePublish} disabled={publishing}>
              {publishing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
              Publish
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-[#00457f] border-b-2 border-[#00457f]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <ValidationPanel validation={validation} />

        {/* Page Builder Tab */}
        {activeTab === 'builder' && (
          <div>
            {/* Page target selector */}
            <div className="flex items-center gap-2 mb-4 border rounded-lg p-1 bg-gray-50 w-fit" data-testid="page-target-selector">
              {[
                { id: 'homepage', label: 'Homepage' },
                { id: 'sponsorship', label: 'Sponsorship' },
                { id: 'golf', label: 'Golf' },
              ].map(p => (
                <button
                  key={p.id}
                  onClick={() => setActivePage(p.id)}
                  data-testid={`page-tab-${p.id}`}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${activePage === p.id ? 'bg-white text-blue-900 shadow-sm font-medium' : 'text-gray-600 hover:text-gray-900'}`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">
                Editing sections for <span className="font-medium">{activePage === 'homepage' ? 'Homepage' : activePage === 'sponsorship' ? 'Sponsorship page' : 'Golf page'}</span>. Drag sections to reorder. Click edit to modify content.
              </p>
              <Button variant="outline" onClick={() => navigate('/admin/layout-builder')} data-testid="open-layout-builder">
                <Layers className="w-4 h-4 mr-1" />
                Layout Builder
              </Button>
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="w-4 h-4 mr-1" />
                Add Section
              </Button>
            </div>

            {sections.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-gray-500 mb-4">No sections yet on this page. Add your first section to get started.</p>
                  <Button onClick={() => setShowAddModal(true)}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Section
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div>
                {sections.map((section) => (
                  <SectionCard
                    key={section.id}
                    section={section}
                    onEdit={setEditingSection}
                    onToggleVisibility={handleToggleVisibility}
                    onDuplicate={handleDuplicate}
                    onDelete={handleDelete}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Media Tab */}
        {activeTab === 'media' && (
          <MediaLibrary />
        )}

        {/* SEO Tab */}
        {activeTab === 'seo' && config && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Search Engine Preview (Google, Bing)</CardTitle>
                <CardDescription>
                  Controls the title + description that appear in Google search results. Google renders JavaScript, so changes here go live after Save — expect them to show up in search within a few hours to a few days as Google re-crawls.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Page Title</Label>
                  <Input
                    value={config.seo?.page_title || ''}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      seo: { ...prev.seo, page_title: e.target.value }
                    }))}
                    placeholder="World Nuclear Congress 2027 | USA"
                    data-testid="seo-page-title"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {(config.seo?.page_title || '').length} / 60 chars — Google truncates past ~60.
                  </p>
                </div>

                <div>
                  <Label>Meta Description</Label>
                  <Textarea
                    value={config.seo?.meta_description || ''}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      seo: { ...prev.seo, meta_description: e.target.value }
                    }))}
                    rows={3}
                    data-testid="seo-meta-description"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {(config.seo?.meta_description || '').length} / 160 chars — Google truncates past ~160.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Keywords</Label>
                    <Input
                      value={config.seo?.keywords || ''}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        seo: { ...prev.seo, keywords: e.target.value }
                      }))}
                      placeholder="World Nuclear Congress, WNC 2027, nuclear energy"
                      data-testid="seo-keywords"
                    />
                  </div>
                  <div>
                    <Label>Canonical URL</Label>
                    <Input
                      value={config.seo?.canonical_url || ''}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        seo: { ...prev.seo, canonical_url: e.target.value }
                      }))}
                      placeholder="https://worldnuclearcongress.org"
                      data-testid="seo-canonical-url"
                    />
                  </div>
                </div>

                {/* Live Google Search Preview */}
                <div className="rounded-lg border bg-gray-50 p-4">
                  <p className="text-[11px] font-semibold uppercase text-gray-500 tracking-wider mb-3">
                    Live preview — Google search result
                  </p>
                  <div className="bg-white rounded-lg border border-gray-200 p-4 max-w-2xl" data-testid="google-preview">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center text-[9px] font-bold text-blue-700">W</div>
                      <div>
                        <p className="text-xs text-gray-900 leading-tight">
                          {config.seo?.site_name || 'World Nuclear Congress'}
                        </p>
                        <p className="text-xs text-gray-500 leading-tight">
                          {(config.seo?.canonical_url || 'https://worldnuclearcongress.org').replace(/^https?:\/\//, '')}
                        </p>
                      </div>
                    </div>
                    <h3 className="text-xl text-[#1a0dab] hover:underline cursor-pointer font-normal leading-tight mb-1" style={{ fontFamily: 'arial, sans-serif' }}>
                      {config.seo?.page_title || '—'}
                    </h3>
                    <p className="text-sm text-gray-600" style={{ fontFamily: 'arial, sans-serif' }}>
                      {(config.seo?.meta_description || '').slice(0, 160) || '—'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Link Preview (iMessage, Slack, Facebook, LinkedIn, WhatsApp)</CardTitle>
                <CardDescription>
                  When someone pastes your site URL into a text message or chat, this is what they'll see. These values ALSO drive Google's social cards. Fallback: if you leave OG Title / OG Description empty, they reuse your Page Title / Meta Description from above.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  <p className="font-semibold mb-1">⚠ Important: iMessage, Slack & WhatsApp don't run JavaScript</p>
                  <p>Changes you save here apply <strong>live for Google</strong>. For iMessage / Slack / WhatsApp link previews, the preview is generated from the static <code className="bg-white/70 px-1 rounded">index.html</code> at the moment the link is sent — those providers also cache aggressively (hours to weeks). To update them you'll need to redeploy, and recipients may need to resend the link.</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Site Name</Label>
                    <Input
                      value={config.seo?.site_name || ''}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        seo: { ...prev.seo, site_name: e.target.value }
                      }))}
                      placeholder="World Nuclear Congress"
                      data-testid="seo-site-name"
                    />
                  </div>
                  <div>
                    <Label>Twitter Card Style</Label>
                    <select
                      value={config.seo?.twitter_card || 'summary_large_image'}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        seo: { ...prev.seo, twitter_card: e.target.value }
                      }))}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="seo-twitter-card"
                    >
                      <option value="summary_large_image">Large Image (recommended)</option>
                      <option value="summary">Small Thumbnail</option>
                    </select>
                  </div>
                </div>

                <div>
                  <Label>Share Title (OG Title)</Label>
                  <Input
                    value={config.seo?.og_title || ''}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      seo: { ...prev.seo, og_title: e.target.value }
                    }))}
                    placeholder={config.seo?.page_title || 'World Nuclear Congress 2027 | USA'}
                    data-testid="seo-og-title"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Leave empty to reuse the Page Title above.
                  </p>
                </div>

                <div>
                  <Label>Share Description (OG Description)</Label>
                  <Textarea
                    value={config.seo?.og_description || ''}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      seo: { ...prev.seo, og_description: e.target.value }
                    }))}
                    rows={2}
                    placeholder={config.seo?.meta_description || ''}
                    data-testid="seo-og-description"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Leave empty to reuse the Meta Description above.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Share Image (OG Image)</Label>
                    <div className="mt-1 space-y-2">
                      <AvatarUpload
                        value={config.seo?.og_image || ''}
                        onChange={(url) => setConfig(prev => ({
                          ...prev,
                          seo: { ...prev.seo, og_image: url }
                        }))}
                        folder="social-share"
                        shape="rect"
                        hint="PNG / JPG · 1200×630 recommended"
                        previewSize={160}
                        testId="seo-og-image-upload"
                      />
                      <Input
                        value={config.seo?.og_image || ''}
                        onChange={(e) => setConfig(prev => ({
                          ...prev,
                          seo: { ...prev.seo, og_image: e.target.value }
                        }))}
                        placeholder="…or paste an external URL"
                        className="font-mono text-xs"
                        data-testid="seo-og-image"
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      1200×630 is the standard size for iMessage / Slack / Facebook cards.
                    </p>
                  </div>
                  <div>
                    <Label>Share Image Alt Text</Label>
                    <Input
                      value={config.seo?.og_image_alt || ''}
                      onChange={(e) => setConfig(prev => ({
                        ...prev,
                        seo: { ...prev.seo, og_image_alt: e.target.value }
                      }))}
                      placeholder="World Nuclear Congress 2027"
                      data-testid="seo-og-image-alt"
                    />
                  </div>
                </div>

                <div>
                  <Label>Twitter Handle</Label>
                  <Input
                    value={config.seo?.twitter_handle || ''}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      seo: { ...prev.seo, twitter_handle: e.target.value }
                    }))}
                    placeholder="@WNCongress"
                    data-testid="seo-twitter-handle"
                  />
                </div>

                {/* Live iMessage Preview */}
                <div className="rounded-lg border bg-gray-50 p-4">
                  <p className="text-[11px] font-semibold uppercase text-gray-500 tracking-wider mb-3">
                    Live preview — iMessage / Slack link card
                  </p>
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm max-w-md" data-testid="imessage-preview">
                    {config.seo?.og_image ? (
                      <div className="aspect-[1200/630] bg-gray-100 overflow-hidden">
                        <img
                          src={config.seo.og_image}
                          alt={config.seo?.og_image_alt || 'Share preview'}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      </div>
                    ) : (
                      <div className="aspect-[1200/630] bg-gradient-to-br from-[#001428] via-[#00457f] to-[#003461] flex items-center justify-center">
                        <p className="text-white text-xs italic opacity-70">No share image set — add one above (1200×630)</p>
                      </div>
                    )}
                    <div className="p-3">
                      <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">
                        {(config.seo?.canonical_url || 'worldnuclearcongress.org').replace(/^https?:\/\//, '').split('/')[0]}
                      </p>
                      <p className="text-sm font-semibold text-gray-900 leading-snug line-clamp-2 mb-1">
                        {config.seo?.og_title || config.seo?.page_title || '—'}
                      </p>
                      <p className="text-xs text-gray-500 leading-snug line-clamp-2">
                        {config.seo?.og_description || config.seo?.meta_description || '—'}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Favicon</CardTitle>
                <CardDescription>The small icon shown next to the page title in browser tabs and Google search results.</CardDescription>
              </CardHeader>
              <CardContent>
                <Label>Favicon URL</Label>
                <Input
                  value={config.seo?.favicon_url || ''}
                  onChange={(e) => setConfig(prev => ({
                    ...prev,
                    seo: { ...prev.seo, favicon_url: e.target.value }
                  }))}
                  placeholder="https://worldnuclearcongress.org/favicon.ico"
                  data-testid="seo-favicon-url"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Leave empty to use the default. Recommended: 32×32 or 192×192 PNG.
                </p>
              </CardContent>
            </Card>

            {/* Per-Page SEO overrides */}
            <Card>
              <CardHeader>
                <CardTitle>Per-Page SEO</CardTitle>
                <CardDescription>
                  Give individual pages their own Google title + description. Any field left blank inherits from the site-wide values above.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                  Per-page overrides apply to <strong>Google search results</strong> (which runs JavaScript) for each page below. iMessage / Slack / WhatsApp link previews always show the site-wide values from the cards above, because those crawlers don't run JS.
                </div>
                {[
                  { path: '/program', label: 'Program Page' },
                  { path: '/sponsorship', label: 'Sponsorship Page' },
                  { path: '/speakers', label: 'Speakers Page' },
                  { path: '/golf-outing', label: 'Golf Outing Page' },
                ].map(({ path, label }) => {
                  const override = (config.page_seo && config.page_seo[path]) || {};
                  const updatePath = (patch) => setConfig(prev => ({
                    ...prev,
                    page_seo: {
                      ...(prev.page_seo || {}),
                      [path]: { ...(prev.page_seo?.[path] || {}), ...patch },
                    },
                  }));
                  return (
                    <div
                      key={path}
                      className="rounded-lg border border-gray-200 bg-white p-4 space-y-3"
                      data-testid={`page-seo-row-${path.replace(/\//g, '-')}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="font-semibold text-sm text-gray-900">{label}</p>
                          <p className="text-xs text-gray-500 font-mono">
                            {(config.seo?.canonical_url || 'https://worldnuclearcongress.org').replace(/\/+$/, '')}{path}
                          </p>
                        </div>
                        {(override.page_title || override.meta_description || override.og_image) && (
                          <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                            Overriding
                          </span>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs">Page Title</Label>
                        <Input
                          value={override.page_title || ''}
                          onChange={(e) => updatePath({ page_title: e.target.value })}
                          placeholder={`${label} · WNC 2027`}
                          data-testid={`page-seo-title-${path.replace(/\//g, '-')}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Meta Description</Label>
                        <Textarea
                          value={override.meta_description || ''}
                          onChange={(e) => updatePath({ meta_description: e.target.value })}
                          rows={2}
                          placeholder={`Describe this page specifically (e.g. "Full WNC 2027 program schedule…")`}
                          data-testid={`page-seo-desc-${path.replace(/\//g, '-')}`}
                        />
                      </div>
                      {(override.page_title || override.meta_description || override.og_title || override.og_description || override.og_image) && (
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfig(prev => {
                              const next = { ...(prev.page_seo || {}) };
                              delete next[path];
                              return { ...prev, page_seo: next };
                            })}
                            className="text-xs text-gray-500 hover:text-red-600"
                            data-testid={`page-seo-clear-${path.replace(/\//g, '-')}`}
                          >
                            <X className="w-3 h-3 mr-1" />
                            Clear override
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            <div className="sticky bottom-4 flex justify-end">
              <Button onClick={() => handleSaveConfig({ seo: config.seo, page_seo: config.page_seo || {} })} size="lg" data-testid="seo-save-btn">
                <Save className="w-4 h-4 mr-2" />
                Save SEO Settings
              </Button>
            </div>
          </div>
        )}

        {/* Video Tab */}
        {activeTab === 'video' && config && (
          <Card>
            <CardHeader>
              <CardTitle>Hero Video Settings</CardTitle>
              <CardDescription>Configure the background video on your hero section</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                <Label>Enable Video Background</Label>
                <Switch
                  checked={config.video?.enabled ?? true}
                  onCheckedChange={(checked) => setConfig(prev => ({
                    ...prev,
                    video: { ...prev.video, enabled: checked }
                  }))}
                />
              </div>
              
              <div>
                <Label>Video Source URL</Label>
                <Input
                  value={config.video?.source_url || ''}
                  onChange={(e) => setConfig(prev => ({
                    ...prev,
                    video: { ...prev.video, source_url: e.target.value }
                  }))}
                  placeholder="/assets/videos/globe-energy.mp4"
                />
              </div>
              
              <div className="flex items-center gap-4">
                <Label>Seamless Crossfade Loop</Label>
                <Switch
                  checked={config.video?.crossfade_enabled ?? true}
                  onCheckedChange={(checked) => setConfig(prev => ({
                    ...prev,
                    video: { ...prev.video, crossfade_enabled: checked }
                  }))}
                />
              </div>
              
              <div>
                <Label>Crossfade Duration (seconds)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={config.video?.crossfade_duration || 1.2}
                  onChange={(e) => setConfig(prev => ({
                    ...prev,
                    video: { ...prev.video, crossfade_duration: parseFloat(e.target.value) }
                  }))}
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between">
                  <Label>Video Opacity</Label>
                  <span className="text-xs text-gray-500 tabular-nums">{Math.round((config.video?.opacity ?? 0.2) * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={config.video?.opacity ?? 0.2}
                  onChange={(e) => setConfig(prev => ({
                    ...prev,
                    video: { ...prev.video, opacity: parseFloat(e.target.value) }
                  }))}
                  className="w-full accent-blue-900"
                  data-testid="video-opacity-slider"
                />
                <p className="text-xs text-gray-500 mt-0.5">How visible the video is. 0% = hidden, 100% = fully visible.</p>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label>Color Overlay Opacity</Label>
                  <span className="text-xs text-gray-500 tabular-nums">{Math.round((config.video?.overlay_opacity ?? 0.4) * 100)}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={config.video?.overlay_opacity ?? 0.4}
                  onChange={(e) => setConfig(prev => ({
                    ...prev,
                    video: { ...prev.video, overlay_opacity: parseFloat(e.target.value) }
                  }))}
                  className="w-full accent-blue-900"
                  data-testid="video-overlay-opacity-slider"
                />
                <p className="text-xs text-gray-500 mt-0.5">Tint layer sitting on top of the video. Higher = darker/more branded.</p>
              </div>

              <div>
                <Label>Color Overlay Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={config.video?.overlay_color || "#001a2e"}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      video: { ...prev.video, overlay_color: e.target.value }
                    }))}
                    className="h-9 w-16 border rounded cursor-pointer"
                    data-testid="video-overlay-color-picker"
                  />
                  <Input
                    type="text"
                    value={config.video?.overlay_color || "#001a2e"}
                    onChange={(e) => setConfig(prev => ({
                      ...prev,
                      video: { ...prev.video, overlay_color: e.target.value }
                    }))}
                    className="w-32 font-mono text-sm"
                    placeholder="#001a2e"
                  />
                  <div className="flex-1 h-9 rounded border" style={{ backgroundColor: config.video?.overlay_color || "#001a2e" }} />
                </div>
              </div>

              <Button onClick={() => handleSaveConfig({ video: config.video })}>
                <Save className="w-4 h-4 mr-1" />
                Save Video Settings
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Versions Tab */}
        {activeTab === 'versions' && (
          <Card>
            <CardHeader>
              <CardTitle>Version History</CardTitle>
              <CardDescription>View and restore previous versions of your homepage</CardDescription>
            </CardHeader>
            <CardContent>
              {versions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">
                  No versions yet. Publish your homepage to create the first version.
                </p>
              ) : (
                <div className="space-y-3">
                  {versions.map((version) => (
                    <div
                      key={version.id}
                      className={`p-4 border rounded-lg ${version.is_current ? 'border-green-500 bg-green-50' : ''}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">
                            Version {version.version_number}
                            {version.is_current && (
                              <span className="ml-2 text-xs bg-green-500 text-white px-2 py-0.5 rounded">
                                Current
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-gray-500">
                            Published: {new Date(version.published_at).toLocaleString()}
                          </p>
                          {version.change_summary?.notes && (
                            <p className="text-sm text-gray-600 mt-1">{version.change_summary.notes}</p>
                          )}
                        </div>
                        {!version.is_current && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRestoreVersion(version.id)}
                          >
                            <RotateCcw className="w-4 h-4 mr-1" />
                            Restore
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Section Editor Drawer */}
        {editingSection && (
          <SectionEditor
            section={editingSection}
            template={templates.find(t => t.id === editingSection.template_id || t.section_type === editingSection.section_type)}
            onSave={handleSaveSection}
            onClose={() => setEditingSection(null)}
          />
        )}

        {/* Add Section Modal */}
        {showAddModal && (
          <AddSectionModal
            templates={templates}
            onAdd={handleAddSection}
            onClose={() => setShowAddModal(false)}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminHomepage;
